import type { Prisma } from "@repo/database";
import { PUBLICLY_FILTERABLE_LISTING_STATUSES } from "../schema-utils";
import type {
  BuyBoxCriteria,
  BuyBoxLocation,
  BuyBoxRange,
} from "./parse-criteria";

/**
 * How far outside a range a disclosed figure may be and still match. Those
 * near misses score partial fit instead of disappearing.
 */
export const BUY_BOX_RANGE_TOLERANCE = 0.2;

type FinancialField = "askingPrice" | "revenue" | "sde" | "ebitda";

/** The widened bounds a disclosed figure must fall inside, or null when unbounded. */
export function toleratedRange(range: BuyBoxRange): BuyBoxRange | null {
  if (range.min == null && range.max == null) return null;
  return {
    min: range.min == null ? null : range.min * (1 - BUY_BOX_RANGE_TOLERANCE),
    max: range.max == null ? null : range.max * (1 + BUY_BOX_RANGE_TOLERANCE),
  };
}

/** A disclosed figure must be near the range; an undisclosed one never excludes. */
function financialClause(
  field: FinancialField,
  range: BuyBoxRange,
): Prisma.ListingWhereInput | null {
  const bounds = toleratedRange(range);
  if (!bounds) return null;
  const filter: Prisma.BigIntNullableFilter = {};
  if (bounds.min != null) filter.gte = BigInt(Math.floor(bounds.min));
  if (bounds.max != null) filter.lte = BigInt(Math.ceil(bounds.max));
  return { OR: [{ [field]: filter }, { [field]: null }] };
}

/** Address subdivisions go four levels deep, so three parent hops cover every ancestor. */
function subdivisionClause(subdivisionId: number): Prisma.AddressWhereInput {
  return {
    OR: [
      { subdivisionId },
      { subdivision: { parentId: subdivisionId } },
      { subdivision: { parent: { parentId: subdivisionId } } },
      { subdivision: { parent: { parent: { parentId: subdivisionId } } } },
    ],
  };
}

function locationClause(location: BuyBoxLocation): Prisma.ListingWhereInput {
  const address: Prisma.AddressWhereInput =
    location.subdivisionId == null
      ? { country: { code: location.countryCode } }
      : subdivisionClause(location.subdivisionId);
  return { business: { address } };
}

/**
 * Marketplace listings that belong in a buy box: live, in the buy box's
 * currency, in one of its business types and locations, and with no
 * disclosed figure far outside its ranges.
 */
export function buildBuyBoxListingWhere(
  criteria: BuyBoxCriteria,
): Prisma.ListingWhereInput {
  const and: Prisma.ListingWhereInput[] = [];

  if (criteria.businessTypeIds.length > 0) {
    and.push({ businessTypeId: { in: criteria.businessTypeIds } });
  }
  if (criteria.locations.length > 0) {
    and.push({ OR: criteria.locations.map(locationClause) });
  }
  for (const field of ["askingPrice", "revenue", "sde", "ebitda"] as const) {
    const clause = financialClause(field, criteria[field]);
    if (clause) and.push(clause);
  }

  return {
    status: { in: [...PUBLICLY_FILTERABLE_LISTING_STATUSES] },
    // Matches link to the public listing page, which needs a slug.
    slug: { not: null },
    currency: criteria.currency,
    ...(and.length > 0 ? { AND: and } : {}),
  };
}
